import { useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import EChartsOperatorGraph from "./EChartsOperatorGraph";
import EChartsGeyserGraph from "./EChartsGeyserGraph";
import DarkDateRangePicker from "./DarkDateRangePicker";

const styles = {
  container: {
    margin: "0 auto",
    padding: "70px 32px 40px",
    maxWidth: "1440px",
  },
};

const QUICK_LINKS = [
  { key: "24h",        label: "Last 24h",      getRange: () => [dayjs().subtract(1, "day"), dayjs()] },
  { key: "7d",         label: "Last 7 days",   getRange: () => [dayjs().subtract(7, "day"), dayjs()] },
  { key: "30d",        label: "Last 30 days",  getRange: () => [dayjs().subtract(30, "day"), dayjs()] },
  { key: "thisMonth",  label: "This month",    getRange: () => [dayjs().startOf("month"), dayjs()] },
  { key: "lastMonth",  label: "Last month",    getRange: () => [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
];

export default function OperatorHistoricalPage() {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [activeQuick, setActiveQuick] = useState("7d");

  const handleApply = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setActiveQuick(null);
  };

  const handleQuickLink = (ql) => {
    const [start, end] = ql.getRange();
    setStartDate(start);
    setEndDate(end);
    setActiveQuick(ql.key);
  };

  // "Last 24h" sends no dates: the endpoint then serves the 24 hours ending now. Dates are
  // whole local days, so it was yesterday 00:00 to now, 24 to 48 hours (NEU-1057).
  const request = {
    queryParams: activeQuick === "24h"
      ? { userTimezone }
      : {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
          userTimezone,
        },
  };

  return (
    <Box style={styles.container}>
      {/* Page header */}
      <Box sx={{ pt: "28px", mb: "24px" }}>
        <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.025em", lineHeight: 1.2 }}>
          Historical demand
        </Typography>
        <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.45)", mt: "4px" }}>
          Custom date range · demand vs target vs forecast
        </Typography>
      </Box>

      <Box sx={{ mb: "20px" }}>
        <DarkDateRangePicker
          startDate={startDate}
          endDate={endDate}
          onApply={handleApply}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: "0.1em", textTransform: "uppercase", mr: "4px" }}>
              Quick
            </Typography>
            {QUICK_LINKS.map((ql) => {
              const isActive = activeQuick === ql.key;
              return (
                <Button
                  key={ql.key}
                  size="small"
                  onClick={() => handleQuickLink(ql)}
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: isActive ? 700 : 500,
                    textTransform: "none",
                    borderRadius: "20px",
                    px: "14px",
                    py: "4px",
                    minWidth: 0,
                    backgroundColor: isActive ? "#FDB940" : "rgba(255,255,255,0.06)",
                    color: isActive ? "#0d1117" : "rgba(255,255,255,0.65)",
                    "&:hover": {
                      backgroundColor: isActive ? "#e5a730" : "rgba(255,255,255,0.12)",
                    },
                  }}
                >
                  {ql.label}
                </Button>
              );
            })}
          </Box>
        </DarkDateRangePicker>
      </Box>

      <EChartsOperatorGraph
        request={request}
        pollingInterval={0}
        title={`Demand · ${startDate.format("D MMM YYYY")} – ${endDate.format("D MMM YYYY")}`}
      />
      <Box sx={{mt:2}}/>
      <EChartsGeyserGraph request={request} pollingInterval={0} />
    </Box>
  );
}
